import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input, Label, Textarea } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Layers, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { PageShell } from "./PageShell";

const BASES = ["combustion", "process", "gwp", "grid", "market", "spend", "supplier"];
const SCOPE1_FALLBACK = [
  { key: "stationary_combustion", label: "Stationary Combustion" },
  { key: "mobile_combustion", label: "Mobile Combustion" },
  { key: "process_emissions", label: "Process Emissions" },
  { key: "fugitive_emissions", label: "Fugitive Emissions" },
];

export default function PlatformGHGCatalog() {
  const [catalog, setCatalog] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ s1: SCOPE1_FALLBACK, s2: [], s3: [] });
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = () => {
    api.get("/ghg/catalog").then((r) => {
      setCatalog(r.data.catalog || []);
      setTaxonomy({
        s1: (r.data.scope1_source_types || SCOPE1_FALLBACK).map((t) => ({ key: t.key, label: t.label })),
        s2: (r.data.scope2_methods || []).map((m) => ({ key: m.key, label: m.label })),
        s3: (r.data.scope3_categories || []).map((c) => ({ key: `cat_${c.cat}`, label: `Cat ${c.cat} · ${c.label}` })),
      });
    }).catch(() => {});
  };
  useEffect(load, []);

  const [facets, setFacets] = useState(null);
  const [importing, setImporting] = useState(false);
  const loadFacets = () => api.get("/emissions/factors/facets").then((r) => setFacets(r.data)).catch(() => {});
  useEffect(() => { loadFacets(); }, []);
  const sefrCount = facets?.registries?.find((r) => r.registry === "SEFR")?.count || 0;
  const ipccCount = facets?.registries?.find((r) => r.registry === "IPCC")?.count || 0;
  const importValues = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/emissions/factors/import-values", fd);
      toast.success(`Imported: ${data.updated} filled · ${data.skipped_published} kept · ${data.not_found} unknown`);
      loadFacets();
    } catch (e) { toast.error(apiErr(e, "Import failed")); }
    finally { setImporting(false); }
  };

  const scopes = useMemo(() => ([
    { scope: 1, label: "Scope 1 — direct emissions", groups: taxonomy.s1 },
    { scope: 2, label: "Scope 2 — purchased energy (methods)", groups: taxonomy.s2 },
    { scope: 3, label: "Scope 3 — value chain (categories)", groups: taxonomy.s3 },
  ]), [taxonomy]);

  const groupsForScope = (scope) => (scopes.find((s) => s.scope === scope) || {}).groups || [];

  const entriesFor = (scope, key) => catalog.filter((c) => c.scope === scope && c.source_type === key);

  const saveEdit = async () => {
    try {
      await api.put(`/ghg/admin/catalog/${edit.code}`, {
        name: edit.name, description: edit.description, unit: edit.unit,
        factor_code: edit.factor_code, basis: edit.basis, source_type: edit.source_type,
      });
      toast.success("Catalog entry updated"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const create = async () => {
    try {
      await api.post("/ghg/admin/catalog", creating);
      toast.success("Catalog entry added — available for every tenant"); setCreating(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete catalog entry “${c.name}”?`)) return;
    try { await api.delete(`/ghg/admin/catalog/${c.code}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openCreate = () => setCreating({
    scope: 1, source_type: "stationary_combustion", name: "", description: "",
    unit: "litre", factor_code: "", basis: "combustion",
  });

  return (
    <PageShell title="GHG Catalog Console" testid="platform-ghg-catalog-page"
      subtitle="The shared GHG Protocol sub-catalog every tenant reads. The fixed scope taxonomy is standard; the operator curates the sub-entries under each Scope 1 source type, Scope 2 method and Scope 3 category (e.g. add ‘kilns’, a solar PPA, or a purchased-goods line). Adding an entry makes it available without touching any tenant’s data or figures."
      actions={<Button variant="primary" size="sm" data-testid="pg-new-entry-btn" onClick={openCreate}>
        <Plus className="h-3.5 w-3.5" /> New catalog entry</Button>}>
      <div className="card-flat p-4 mb-2" data-testid="sefr-values-importer">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Upload className="h-4 w-4 text-accent" />
          <span className="font-display font-bold text-sm">SEFR Values Importer</span>
          <Badge variant="lime" className="text-[9px]">SEFR · SG · {sefrCount}</Badge>
          <Badge variant="secondary" className="text-[9px]">IPCC · intl · {ipccCount}</Badge>
          {facets?.pending_count > 0 && <Badge variant="warning" className="text-[9px]">{facets.pending_count} value pending</Badge>}
        </div>
        <p className="text-[11px] text-muted-foreground mb-2">One-shot load of the SEFR values file (.csv / .xlsx) to fill “value pending” entries by factor code. Existing values and catalogue structure are untouched.</p>
        <input type="file" accept=".csv,.xlsx,.xlsm" data-testid="sefr-import-file" disabled={importing}
          onChange={(e) => importValues(e.target.files?.[0])}
          className="block w-full max-w-md text-xs file:mr-3 file:rounded-sm file:border-0 file:bg-accent/15 file:px-3 file:py-2 file:text-accent file:font-medium" />
        {importing && <div className="text-[11px] text-muted-foreground mt-2">Importing…</div>}
      </div>
      {scopes.map((s) => (
        <div key={s.scope} className="space-y-3" data-testid={`pg-scope-${s.scope}`}>
          <div className="label-eyebrow text-[11px] pt-2">{s.label}</div>
          {s.groups.map((t) => (
            <div key={t.key} className="card-flat p-4" data-testid={`pg-type-${t.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <Layers className="h-4 w-4 text-accent" />
                <span className="font-display font-bold text-sm">{t.label}</span>
                <Badge variant="secondary" className="text-[9px] font-mono">Scope {s.scope}</Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {entriesFor(s.scope, t.key).map((c) => (
                  <div key={c.code} className="border border-border rounded-md p-3" data-testid={`pg-entry-${c.code}`}>
                    <div className="flex items-center gap-2">
                      <Badge variant="lime" className="font-mono text-[9px]">{c.code}</Badge>
                      <span className="font-medium text-sm">{c.name}</span>
                      {c.system && <Badge variant="secondary" className="text-[9px]">seed</Badge>}
                      <div className="ml-auto flex gap-1">
                        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setEdit({ ...c })} data-testid={`pg-edit-${c.code}`}><Pencil className="h-2.5 w-2.5" /></Button>
                        {!c.system && <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-400" onClick={() => del(c)} data-testid={`pg-del-${c.code}`}><Trash2 className="h-2.5 w-2.5" /></Button>}
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">{c.description}</p>
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono">unit: {c.unit} · factor: {c.factor_code || "—"} · basis: {c.basis}</div>
                  </div>
                ))}
                {!entriesFor(s.scope, t.key).length && <div className="text-xs text-muted-foreground">No entries yet.</div>}
              </div>
            </div>
          ))}
          {!s.groups.length && <div className="text-xs text-muted-foreground card-flat p-4">Taxonomy unavailable.</div>}
        </div>
      ))}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="pg-edit-dialog">
          <DialogHeader><DialogTitle>Edit {edit?.code}</DialogTitle></DialogHeader>
          {edit && <EntryForm entry={edit} setEntry={setEdit} groups={groupsForScope(edit.scope)} isEdit onSave={saveEdit} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent data-testid="pg-create-dialog">
          <DialogHeader><DialogTitle>New catalog entry</DialogTitle></DialogHeader>
          {creating && <EntryForm entry={creating} setEntry={setCreating} scopes={scopes} onSave={create} />}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

function EntryForm({ entry, setEntry, scopes, groups, isEdit, onSave }) {
  const set = (k, v) => setEntry({ ...entry, [k]: v });
  const scopeGroups = isEdit ? groups : ((scopes.find((s) => s.scope === entry.scope) || {}).groups || []);

  const pickScope = (v) => {
    const scope = Number(v);
    const g = (scopes.find((s) => s.scope === scope) || {}).groups || [];
    const unit = scope === 2 ? "kWh" : scope === 3 ? "kg" : "litre";
    const basis = scope === 2 ? "market" : scope === 3 ? "supplier" : "combustion";
    setEntry({ ...entry, scope, source_type: g[0]?.key || "", unit, basis });
  };

  return (
    <div className="space-y-3">
      {!isEdit && (
        <div><Label>Scope</Label>
          <Select value={String(entry.scope)} onValueChange={pickScope}>
            <SelectTrigger data-testid="pg-form-scope"><SelectValue /></SelectTrigger>
            <SelectContent>{scopes.map((s) => <SelectItem key={s.scope} value={String(s.scope)}>{`Scope ${s.scope}`}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div><Label>{entry.scope === 2 ? "Method" : entry.scope === 3 ? "Category" : "Source type"}</Label>
        <Select value={entry.source_type} onValueChange={(v) => set("source_type", v)}>
          <SelectTrigger data-testid="pg-form-source-type"><SelectValue /></SelectTrigger>
          <SelectContent>{scopeGroups.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Name *</Label><Input value={entry.name} onChange={(e) => set("name", e.target.value)} data-testid="pg-form-name" /></div>
      <div><Label>Description</Label><Textarea rows={2} value={entry.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="pg-form-desc" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Unit</Label><Input value={entry.unit} onChange={(e) => set("unit", e.target.value)} data-testid="pg-form-unit" /></div>
        <div><Label>Basis</Label>
          <Select value={entry.basis} onValueChange={(v) => set("basis", v)}>
            <SelectTrigger data-testid="pg-form-basis"><SelectValue /></SelectTrigger>
            <SelectContent>{BASES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>SEFR factor code (optional)</Label><Input value={entry.factor_code || ""} onChange={(e) => set("factor_code", e.target.value)} data-testid="pg-form-factor" placeholder="e.g. SEFR-EL-SG-2024" /></div>
      <Button variant="primary" onClick={onSave} disabled={!entry.name || !entry.source_type} data-testid="pg-form-save">Save</Button>
    </div>
  );
}
